import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiUpload, FiFileText, FiImage, FiTrash2, FiExternalLink, FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import panelStyles from '../../styles/adminPanels.module.css';
import styles from './AdminCompanyDocuments.module.css';
import axiosClient from '../../api/axiosClient';

interface Doc { id: string; title: string; file_path: string; category: string; created_at: string; }

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');

const CATEGORIES = [
  { key: 'company', label: 'Company Documents', hint: 'Certificate of Incorporation, PAN, MOA, AOA, etc. — shown in "Company documents" on the landing page.' },
  { key: 'product', label: 'Our Product', hint: 'Product decks, whitepapers, technical docs — shown in "Our product" on the landing page.' },
  { key: 'pitch_deck', label: 'Our Pitch Deck', hint: 'The pitch deck — shown in "Our pitch deck" on the landing page. Starts empty until you upload one.' },
];

const AdminCompanyDocuments: React.FC = () => {
  const [category, setCategory] = useState('company');
  const [docs, setDocs] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [renaming, setRenaming] = useState(false);

  const load = (cat: string) => {
    setLoading(true);
    axiosClient.get('/company-documents', { params: { category: cat } }).then(({ data }) => setDocs(data.documents)).finally(() => setLoading(false));
  };
  useEffect(() => { load(category); }, [category]);

  const activeCat = CATEGORIES.find((c) => c.key === category)!;

  const handleUpload = async () => {
    if (!title.trim()) { toast.error('Enter a document title.'); return; }
    if (!file) { toast.error('Choose a file to upload.'); return; }
    setSaving(true);
    try {
      const form = new FormData();
      form.append('title', title);
      form.append('category', category);
      form.append('file', file);
      await axiosClient.post('/admin/company-documents', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Uploaded — now live under "${activeCat.label}" on the landing page.`);
      setTitle(''); setFile(null);
      load(category);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not upload document.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await axiosClient.delete(`/admin/company-documents/${id}`);
      toast.success('Document removed.');
      load(category);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not remove document.');
    }
  };

  const startEdit = (d: Doc) => {
    setEditingId(d.id);
    setEditTitle(d.title);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
  };
  const saveEdit = async (id: string) => {
    if (!editTitle.trim()) { toast.error('Enter a document title.'); return; }
    setRenaming(true);
    try {
      await axiosClient.patch(`/admin/company-documents/${id}`, { title: editTitle.trim() });
      toast.success('Document renamed.');
      setEditingId(null);
      load(category);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not rename document.');
    } finally {
      setRenaming(false);
    }
  };

  return (
    <AdminShell>
      <PageHeader title="Documents" subtitle="Manage the documents shown publicly on the landing page, across three sections." action={<AdminWalletBadge />} />

      <div className={panelStyles.page}>
        <div className={styles.catTabs}>
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              className={category === c.key ? styles.catTabActive : styles.catTab}
              onClick={() => setCategory(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className={styles.uploadCard}>
          <h3>Upload to "{activeCat.label}"</h3>
          <div className={styles.uploadRow}>
            <input
              className={styles.titleInput}
              placeholder="Document title (e.g. GST Certificate)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <label className={styles.fileBtn}>
              <FiUpload /> {file ? file.name : 'Choose file'}
              <input type="file" accept="application/pdf,image/png,image/jpeg" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <button className={styles.uploadBtn} onClick={handleUpload} disabled={saving}>
              {saving ? 'Uploading…' : 'Upload'}
            </button>
          </div>
          <div className={styles.hint}>PDF, PNG, or JPG — up to 60MB. {activeCat.hint}</div>
        </div>

        <div className={panelStyles.tableWrap}>
          {loading && <div className={panelStyles.loading}><span className={panelStyles.spinnerLg} /></div>}
          {!loading && docs.length === 0 && <div className={panelStyles.empty}>No documents in "{activeCat.label}" yet.</div>}
          {!loading && docs.length > 0 && (
            <table className={panelStyles.table}>
              <thead><tr><th>Document</th><th>Uploaded</th><th>Actions</th></tr></thead>
              <tbody>
                {docs.map((d) => {
                  const isPdf = /\.pdf$/i.test(d.file_path);
                  const isEditing = editingId === d.id;
                  return (
                    <tr key={d.id}>
                      <td>
                        {isEditing ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {isPdf ? <FiFileText /> : <FiImage />}
                            <input
                              className={styles.titleInput}
                              style={{ maxWidth: 260 }}
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(d.id); if (e.key === 'Escape') cancelEdit(); }}
                              autoFocus
                            />
                          </div>
                        ) : (
                          <div className={panelStyles.nameCell} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {isPdf ? <FiFileText /> : <FiImage />} {d.title}
                          </div>
                        )}
                      </td>
                      <td className={panelStyles.subCell}>{new Date(d.created_at).toLocaleDateString()}</td>
                      <td>
                        {isEditing ? (
                          <>
                            <button className={panelStyles.actionBtn} style={{ marginRight: 8 }} onClick={() => saveEdit(d.id)} disabled={renaming}>
                              <FiCheck /> {renaming ? 'Saving…' : 'Save'}
                            </button>
                            <button className={panelStyles.actionBtnReject} onClick={cancelEdit}>
                              <FiX /> Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <a
                              className={panelStyles.actionBtn}
                              style={{ marginRight: 8, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                              href={`${API_ORIGIN}${d.file_path}`}
                              target="_blank" rel="noreferrer"
                            >
                              <FiExternalLink /> View
                            </a>
                            <button className={panelStyles.actionBtn} style={{ marginRight: 8 }} onClick={() => startEdit(d)}>
                              <FiEdit2 /> Edit
                            </button>
                            <button className={panelStyles.actionBtnReject} onClick={() => handleDelete(d.id)}>
                              <FiTrash2 /> Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminCompanyDocuments;
